<!-- MatchReport.vue -->
<template>
  <div class="cl-page">
    <!-- Fixed Header -->
    <div class="cl-toolbar">
      <v-toolbar flat density="comfortable" class="bg-transparent elevation-0">
        <v-toolbar-title class="cl-toolbar-title">
          {{ isNew ? 'Add New Match Report' : 'Edit Match Report' }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn variant="tonal" class="mr-2" prepend-icon="mdi-magnify" @click="openGameSearch = true">
          Game Search
        </v-btn>
        <v-btn color="success" :loading="saving" prepend-icon="mdi-content-save" @click="onSave(false)" class="save-button">
          {{ isNew ? 'Save Report' : 'Save Report' }}
        </v-btn>
        <v-btn variant="text" class="ml-2" prepend-icon="mdi-close" @click="onCancel">Cancel / Exit</v-btn>
      </v-toolbar>
    </div>

    <!-- Scrollable Content Area -->
    <div class="cl-content-area">
      <div class="cl-form">
        <v-container fluid class="py-0 px-0">
          <v-form ref="formRef" v-model="valid" @submit.prevent="onSave" class="form-content" autocomplete="off">
            <!-- Top row: Game metadata -->
            <div class="report-section">
              <v-row dense>
                <v-col cols="12" md="1">
                  <v-text-field label="Game #" v-model="mreport.GameNumber" density="comfortable" :rules="[req]" hide-details="auto" />
                </v-col>
                <v-col cols="12" md="auto" style="flex-basis: 10%">
                  <v-text-field label="Date" v-model="mreport.GameDate" type="date" density="comfortable" :rules="[req]" hide-details="auto" />
                </v-col>
                <v-col cols="12" md="1">
                  <v-select label="Time" v-model="mreport.GameTime" :items="timeOptions" density="comfortable" :rules="[req]" hide-details="auto" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select label="Venue" v-model="mreport.Venue" density="comfortable" hide-details="auto" />
                </v-col>
                <v-spacer />
                <v-col cols="12" md="1">
                  <v-text-field label="Group / League" v-model="mreport.League" density="comfortable" :rules="[req]" hide-details="auto" />
                </v-col>
                <v-col cols="12" md="2">
                  <v-select label="GenderAge" v-model="mreport.GenderAge" 
                    :items="filteredGenderAges" :item-title="gaLabel" item-value="GenderAge"
                    density="comfortable" :disabled="!mreport.League" :rules="[req]" hide-details="auto"/>
                </v-col>
                <v-col cols="12" md="2">
                  <v-select label="League Type & Season" v-model="mreport.LeagueTypeSeason"
                    :items="filteredLeagues" item-title="LeagueTypeSeason" item-value="LeagueTypeSeason"
                    density="comfortable" :rules="[req]" hide-details="auto"/>
                </v-col>
              </v-row>

              <v-row class="pl-10">
                <!-- Left Column -->
                <v-col cols="12" md="7">
                  <v-row dense class="test-left">
                    <v-col md="4"><div>Teams</div></v-col>
                    <v-col md="2"><div>Color</div></v-col>
                    <v-col md="auto" style="flex-basis: 10%"><div>Score</div></v-col>
                    <v-col md="2"><div>Details</div></v-col>
                  </v-row>

                  <v-row dense class="pl-5">
                    <v-col cols="12" md="4">
                      <v-text-field v-model="mreport.TeamHome" label="Team Home" density="comfortable" :rules="[req]" hide-details="auto" />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select label="Home Color" v-model="mreport.TeamColorHome" :items="colorOptions" density="comfortable" hide-details="auto" />
                    </v-col>
                    <v-col cols="12" md="auto" style="flex-basis: 10%">
                      <v-select label="Home" v-model.number="mreport.ScoreHome" :items="scoreOptions" density="comfortable" hide-details="auto" :rules="[req]" />
                    </v-col>
                    <v-col cols="12" md="2" class="d-flex align-center">
                      <v-checkbox v-model="mreport.Overtime" label="Overtime?" hide-details />
                    </v-col>
                  </v-row>

                  <v-row dense class="pl-5">
                    <v-col cols="12" md="4">
                      <v-text-field v-model="mreport.TeamAway" label="Team Away" density="comfortable" :rules="[req]" hide-details="auto" />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select label="Away Color" v-model="mreport.TeamColorAway" :items="colorOptions" density="comfortable" hide-details="auto" />
                    </v-col>
                    <v-col cols="12" md="auto" style="flex-basis: 10%">
                      <v-select label="Away" v-model.number="mreport.ScoreAway" :items="scoreOptions" density="comfortable" hide-details="auto" :rules="[req]" />
                    </v-col>
                    <v-col cols="6" md="2" class="d-flex align-center">
                      <v-checkbox v-model="mreport.Penalties" label="PKs?" hide-details />
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Right Column -->
                <v-col cols="12" md="5">
                  <v-textarea label="Notes / Description" v-model="mreport.Notes" rows="5" auto-grow />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" md="3">
                  <v-autocomplete label="REF / Dual1" v-model="mreport.REFName"
                    :items="users" item-title="fullName" item-value="fullName"
                    density="comfortable" auto-select-first hide-details="auto" :chips="false"
                    clearable name="REFname-no-autofill" autocomplete="off" autocapitalize="none"
                    autocorrect="off" :spellcheck="false" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete label="AR1 / Dual2" v-model="mreport.AR1Name"
                    :items="users" item-title="fullName" item-value="fullName"
                    density="comfortable" auto-select-first hide-details="auto" :chips="false"
                    clearable name="AR1name-no-autofill" autocomplete="off" autocapitalize="none"
                    autocorrect="off" :spellcheck="false" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete label="AR2" v-model="mreport.AR2Name"
                    :items="users" item-title="fullName" item-value="fullName"
                    density="comfortable" auto-select-first hide-details="auto" :chips="false"
                    clearable name="AR2name-no-autofill" autocomplete="off" autocapitalize="none"
                    autocorrect="off" :spellcheck="false" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete label="4th Official" v-model="mreport.REF4THName"
                    :items="users" item-title="fullName" item-value="fullName"
                    density="comfortable" auto-select-first hide-details="auto" :chips="false"
                    clearable name="REF4THname-no-autofill" autocomplete="off" autocapitalize="none"
                    autocorrect="off" :spellcheck="false"/>
                </v-col>
              </v-row>

              <v-row class="align-center mb-0">
                <v-col class="flex-grow-0">
                  <v-card-title class="text-subtitle-1 font-medium text-blue-darken-2 pa-0">
                    Behavior Assessments
                  </v-card-title>
                </v-col>
                <v-col cols="auto">
                  <v-btn color="info" variant="tonal" prepend-icon="mdi-information" @click="ratingDialog = true">
                    Rating Scale
                  </v-btn>
                </v-col>
                <v-col>
                  <div style="height: 4px; background-color: #1976d2;"></div>
                </v-col>
              </v-row>

              <v-row dense class="pl-10">
                <v-col cols="12" md="6">
                  <v-row dense class="text-left">
                    <v-col md="2"><div>Coach (Home)</div></v-col>
                    <v-col md="3"><v-select v-model="mreport.BhvrCoachHomeRating" :items="bhvrRatings" label="Rating" density="comfortable" :rules="[req]" /></v-col>
                    <v-col md="7"><v-textarea v-model="mreport.BhvrCoachHomeNotes" label="Notes" rows="2" auto-grow density="comfortable" /></v-col>
                  </v-row>
                  <v-row dense class="text-left">
                    <v-col md="2"><div>Coach (Away)</div></v-col>
                    <v-col md="3"><v-select v-model="mreport.BhvrCoachAwayRating" :items="bhvrRatings" label="Rating" density="comfortable" :rules="[req]" /></v-col>
                    <v-col md="7"><v-textarea v-model="mreport.BhvrCoachAwayNotes" label="Notes" rows="2" auto-grow density="comfortable" /></v-col>
                  </v-row>
                </v-col>

                <v-col cols="12" md="6">
                  <v-row dense class="text-left">
                    <v-col md="2"><div>Spectators (Home)</div></v-col>
                    <v-col cols="12" md="3"><v-select v-model="mreport.BhvrSpectatorHomeRating" :items="bhvrRatings" label="Rating" density="comfortable" :rules="[req]" /></v-col>
                    <v-col cols="12" md="7"><v-textarea v-model="mreport.BhvrSpectatorHomeNotes" label="Notes" rows="2" auto-grow density="comfortable" /></v-col>
                  </v-row>
                  <v-row dense class="text-left">
                    <v-col md="2"><div>Spectators (Away)</div></v-col>
                    <v-col cols="12" md="3"><v-select v-model="mreport.BhvrSpectatorAwayRating" :items="bhvrRatings" label="Rating" density="comfortable" :rules="[req]" /></v-col>
                    <v-col cols="12" md="7"><v-textarea v-model="mreport.BhvrSpectatorAwayNotes" label="Notes" rows="2" auto-grow density="comfortable" /></v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row dense class="align-center mb-0">
                <v-col class="flex-grow-0">
                  <v-card-title class="text-subtitle-1 font-medium text-blue-darken-2 pa-0">Misconduct</v-card-title>
                </v-col>
                <v-col>
                  <div style="height: 4px; background-color: #1976d2;"></div>
                </v-col>
              </v-row>
              <v-row dense class="justify-start mb-0">
                <v-col cols="auto">
                  <v-btn @click="onAddMisconduct" color="orange-darken-2" prepend-icon="mdi-plus">Add Misconduct Report</v-btn>
                </v-col>
                <v-spacer />
                <v-col v-if="misconductReports.length > 0" cols="auto">
                  <GenerateCIFLoadReportPDF :mreportId="mreportId || reportId">Generate CIF PDF</GenerateCIFLoadReportPDF>
                </v-col>
              </v-row>
            </div>

            <!-- Misconduct Section -->
            <section class="misconduct-section">
              <v-card flat class="misconduct-card text-left">
                <v-row justify="start">
                  <v-col cols="12">
                    <v-data-table 
                      :headers="misconductHeaders" 
                      :items="misconductReports" 
                      :item-class="getMisconductRowClass" 
                      item-key="id" 
                      :item-value="r => r.id"
                      fixed-header 
                      height="500px"
                      density="compact" 
                      :items-per-page="-1" 
                      hide-default-footer 
                      class="fixed-layout elevation-1 w-100" 
                      @click:row="onRowClick">
                      <template v-slot:[`item`]="{ item }">
                        <tr :class="getMisconductRowClass(item)">
                          <td style="width: 40px;">{{ item.Time }}</td>
                          <td style="width: 60px;">{{ item.Offender }}</td>
                          <td style="width: 140px;">{{ item.Team }}</td>
                          <td style="width: 220px;">({{ item.PlayerNumber }}) {{ item.PlayerNameLast }}, {{ item.PlayerNameFirst }}</td>
                          <td style="width: 140px;">{{ item.IncidentType }}</td>
                          <td style="width: 320px;">{{ item.IncidentDescription }}</td>
                          <td style="width: 140px;">
                            <v-chip :color="actionColor(item.RefereeAction)" size="small" label>
                              {{ item.RefereeAction }}
                            </v-chip>
                          </td>
                          <td style="width: 220px;">{{ item.MisconductItem }}</td>
                          <td style="width: 120px;">
                            <div class="d-flex ga-2" @click.stop>
                              <v-btn icon="mdi-pencil" size="small" variant="text" @click="onEditMisconduct(item.id)" />
                              <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="onDeleteMisconduct(item.id)" />
                            </div>
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-card>
            </section>
          </v-form>
        </v-container>
      </div>
    </div>

    <!-- Game Search Dialog -->
    <v-dialog v-model="openGameSearch" max-width="1100">
      <v-card>
        <v-card-title class="text-h6">GAME SEARCH</v-card-title>
        <v-card-subtitle>
          Games are listed by latest date.
          <v-chip label size="x-small" color="yellow-darken-2">Highlighted = report exists</v-chip>
        </v-card-subtitle>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="searchDate" label="Filter by date" type="date" clearable @update:modelValue="filterGames" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="searchRefName" label="Filter by referee last name" clearable @update:modelValue="filterGames" />
            </v-col>
          </v-row>
          <div class="game-search-table-wrapper">
            <v-table density="compact" fixed-header height="420">
              <thead>
                <tr>
                  <th style="width: 120px; min-width: 120px;">Select</th>
                  <th style="width: 100px; min-width: 100px;">Game #</th>
                  <th style="width: 120px; min-width: 120px;">Date</th>
                  <th style="width: 150px; min-width: 150px;">League</th>
                  <th style="width: 150px; min-width: 150px;">GenderAge</th>
                  <th style="width: 300px; min-width: 300px;">Teams</th>
                  <th style="width: 200px; min-width: 200px;">REF</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="g in filteredGames" :key="g.GameNumber + '_' + g.GameDate" :class="g.MatchReportSubmitted ? 'bg-yellow-lighten-4' : ''">
                  <td style="width: 120px; min-width: 120px;"><v-btn size="small" color="primary" variant="tonal" @click="selectGame(g)">Select</v-btn></td>
                  <td style="width: 100px; min-width: 100px;">{{ g.GameNumber }}</td>
                  <td style="width: 120px; min-width: 120px;">{{ formatDate(g.GameDate) }}</td>
                  <td style="width: 150px; min-width: 150px;">{{ g.League }}</td>
                  <td style="width: 150px; min-width: 150px;">{{ g.GenderAge }}</td>
                  <td style="width: 300px; min-width: 300px;">{{ g.TeamHome }} vs. {{ g.TeamAway }}</td>
                  <td style="width: 200px; min-width: 200px;">{{ g.REF }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="openGameSearch = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add/Edit Misconduct Dialog -->
    <v-dialog v-model="misconductDialog.open" max-width="980" scrollable :retain-focus="false">
      <MisconductForm
        v-if="misconductDialog.open"
        :key="misconductDialog.miscId || 'new'"
        :mreport-id="mreportId || reportId"
        :misc-id="misconductDialog.miscId"
        :team-home="mreport.TeamHome"
        :team-away="mreport.TeamAway"
        :league="mreport.League"
        :report-user-id="mreport.UserID" 
        :user-id="currentUserID"
        @saved="onMisconductSaved"
        @closed="onMisconductClosed"
      />
    </v-dialog>

    <v-dialog v-model="ratingDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h6">Behavior Rating Scale</v-card-title>
        <v-card-text>
          <div class="rating-scale-text">
            <p><strong>Excellent:</strong> Displays outstanding behavior consistently. Demonstrates exceptional qualities, manners, and positive actions.</p>
            <p><strong>Good:</strong> Shows commendable behavior on a regular basis. Acts responsibly, shows respect, and engages in positive interactions.</p>
            <p><strong>Satisfactory:</strong> Generally meets expectations with occasional lapses in behavior. Requires some improvement in certain areas.</p>
            <p><strong>Needs Improvement:</strong> Frequently exhibits undesirable behavior. Requires significant improvement and correction in various aspects.</p>
            <p><strong>Unsatisfactory:</strong> Demonstrates consistently poor behavior. Requires immediate attention and intervention to address the issues.</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="ratingDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { db, gamescheduleColRef, leaguesColRef, matchreportsColRef, refereesColRef, genderagesColRef, misconductColRef } from '@/firebase'
import { doc, getDoc, updateDoc, getDocs, addDoc, query, where, deleteDoc, Timestamp, setDoc } from 'firebase/firestore'
import MisconductForm from '@/components/MisconductForm.vue'
import { useRoleCaps } from '../composables/useRoleCaps'
import GenerateCIFLoadReportPDF from '@/components/GenerateCIFLoadReportPDF.vue'

defineOptions({ name: 'EditReportWithMisconductDialog' })

// --- state ---
const route = useRoute()
const router = useRouter()
const auth = getAuth()
const { caps } = useRoleCaps()

const isNew = ref(route.params.id === 'NEW' || route.query.isNew === 'true')
let mreportId = route.params.id && route.params.id !== 'NEW' ? route.params.id : null
const reportId = computed(() => route.params.id || route.params.mreportId)

// 🔍 Capture selectedUserId and selectedUserOrg from query params (for Admin adding reports)
const selectedUserID = ref(route.query.selectedUserId ? String(route.query.selectedUserId) : '')
const selectedUserOrg = ref(route.query.selectedUserOrg ? String(route.query.selectedUserOrg) : '')

const saving = ref(false)
const valid = ref(false)
const formRef = ref(null)

const mreport = reactive({
  GameNumber: '',
  GameDate: null,
  GameTime: '',
  Venue: '',
  GenderAge: '',
  Organization: '',
  League: '',
  LeagueTypeSeason: '',
  TeamHome: '',
  TeamAway: '',
  TeamColorHome: '',
  TeamColorAway: 'White',
  ScoreHome: null,
  ScoreAway: null,
  Overtime: false,
  Penalties: false,
  MatchComments: '',
  REFName: '',
  AR1Name: '',
  AR2Name: '',
  REF4THName: '',
  BhvrCoachHomeRating: '',
  BhvrCoachHomeNotes: '',
  BhvrCoachAwayRating: '',
  BhvrCoachAwayNotes: '',
  BhvrSpectatorHomeRating: '',
  BhvrSpectatorHomeNotes: '',
  BhvrSpectatorAwayRating: '',
  BhvrSpectatorAwayNotes: '',
  YellowCardCount: 0,
  RedCardCount: 0,
  PurpleCardCount: 0,
  DoublePurpleCardCount: 0,
})

const currentUserID = ref('')
const currentUserOrgName = ref('')

const users = ref([])
const leagues = ref([])
const genderages = ref([])
const games = ref([])
const filteredGames = ref([])
const searchDate = ref('')
const searchRefName = ref('')
const openGameSearch = ref(false)
const misconductReports = ref([])
const ratingDialog = ref(false)

// --- options ---
const timeOptions = ref([
  '0600','0615','0630','0645','0700','0715','0730','0745','0800','0815','0830','0845',
  '0900','0915','0930','0945','1000','1015','1030','1045','1100','1115','1130','1145',
  '1200','1215','1230','1245','1300','1315','1330','1345','1400','1415','1430','1445',
  '1500','1515','1530','1545','1600','1615','1630','1645','1700','1715','1730','1745',
  '1800','1815','1830','1845','1900','1915','1930','1945','2000','2015','2030','2045','2100'
])
const scoreOptions = Array.from({ length: 16 }, (_, i) => i)
const colorOptions = ['Dark Blue','Light Blue','Burgundy','Green','Orange','Red','Black','Gray','Yellow','Purple','Pink','White']
const bhvrRatings = ['Excellent','Good','Satisfactory','Needs Improvement','Unsatisfactory']

const misconductHeaders = [
  { title: 'Time', key: 'Time' },
  { title: 'Offender', key: 'Offender'},
  { title: 'Team', key: 'Team' },
  { title: '# / Name', key: 'player', width: 220 },
  { title: 'Incident Type', key: 'IncidentType', width: 140 },
  { title: 'Incident Description', key: 'IncidentDescription', width: 320 },
  { title: 'Referee Action', key: 'RefereeAction', width: 140 },
  { title: 'Misconduct Item', key: 'MisconductItem', width: 220 },
  { title: 'Actions', key: 'actions', sortable: false, width: 120 },
]

// --- validation ---
const req = (v) => (v === 0 || !!v) || 'Required'

// --- computed ---
const filteredLeagues = computed(() => {
  if (!mreport.League) return leagues.value
  return leagues.value.filter(l => l.League === mreport.League)
})
const gaLabel = (ga) => `${ga.GenderAge} - ${ga.GADescription ?? ''}`
const filteredGenderAges = computed(() => {
  if (!mreport.League) return []
  return [...genderages.value]
    .filter(ga => ga.League === mreport.League)
    .sort((a, b) => b.GenderAge.localeCompare(a.GenderAge, undefined, { numeric: true }))
})
const actionColor = (action) => {
  if (action === 'Yellow Card') return 'yellow-darken-2'
  if (action === 'Red Card') return 'red-darken-2'
  if (action === 'Purple Card') return 'deep-purple-darken-2'
  if (action === 'Double Purple Card') return 'deep-purple'
  if (action === 'WARN') return 'orange-darken-2'
  if (action === 'REMOVAL') return 'red-darken-4'
  return 'grey'
}

// Dialog state
const misconductDialog = reactive({ open: false, miscId: null })

// --- lifecycle ---
onMounted(async () => {
  await initAuth()
  
  try {
    await validateUserAccess();
  } catch (error) {
    console.error("Access validation failed:", error);
    alert(error.message);
    router.push('/login');
    return;
  }
  
  await Promise.all([buildRefereeListInSameOrgName(), fetchGenderAges(), fetchLeagues()])

  if (isNew.value) {
    console.log('🔍 Initializing NEW report with:')
    console.log('  - UserID:', currentUserID.value)
    console.log('  - Organization:', currentUserOrgName.value)
    
    Object.assign(mreport, {
      GameDate: new Date().toISOString().split('T')[0],
      ScoreHome: null,
      ScoreAway: null,
      Organization: currentUserOrgName.value,
      UserID: currentUserID.value,
      TeamColorAway: 'White',
    })
    openGameSearch.value = true
  } else {
    await fetchMatchReportData()
    await fetchMisconductData()
  }
})

async function validateUserAccess() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Not authenticated");
  }

  await user.getIdToken(true);
  const idTokenResult = await user.getIdTokenResult(true);
  const claims = idTokenResult.claims;

  console.log("MatchReport - Validating claims:", claims);

  if (!claims || !claims.roles) {
    throw new Error("No access roles found. Please contact administrator.");
  }

  const storedOrg = localStorage.getItem("currentUserOrgName");
  const storedRole = localStorage.getItem("currentUserRole");

  if (!storedOrg || !storedRole) {
    throw new Error("Session data missing. Please log in again.");
  }

  const rolesMap = claims.orgRoles || {};
  const userRoleForOrg = rolesMap[storedOrg];

  if (!userRoleForOrg) {
    const availableOrgs = Object.keys(rolesMap).join(", ") || "(none)";
    throw new Error(
      `You no longer have access to "${storedOrg}". Available organizations: ${availableOrgs}`
    );
  }

  if (!isNew.value && mreportId) {
    const reportDoc = await getDoc(doc(db, 'matchreports', mreportId));
    if (reportDoc.exists()) {
      const reportData = reportDoc.data();
      const reportUserID = reportData.UserID;
      const reportOrg = reportData.Organization;

      const isOwner = reportUserID === user.uid;
      const isAdmin = claims.role === 'ADMIN';
      const sameOrg = reportOrg === storedOrg;

      if (!isOwner && !isAdmin) {
        throw new Error("You don't have permission to edit this report.");
      }

      if (!sameOrg && !isAdmin) {
        throw new Error("This report belongs to a different organization.");
      }
    }
  }

  return true;
}

// --- auth & user/org ---
async function initAuth() {
  return new Promise((resolve) => {
    onAuthStateChanged(auth, (user) => {
      if (!user) {
        router.push('/login')
        return resolve()
      }
      
      // 🔍 If Admin is creating a report for a selected user, use selectedUserID
      // Otherwise, use the current authenticated user's ID
      if (isNew.value && selectedUserID.value) {
        currentUserID.value = selectedUserID.value
        currentUserOrgName.value = selectedUserOrg.value
        console.log('🔍 ADMIN MODE: Creating report for selected user')
        console.log('  - selectedUserID:', selectedUserID.value)
        console.log('  - selectedUserOrg:', selectedUserOrg.value)
      } else {
        currentUserID.value = user.uid
        const orgName = localStorage.getItem('currentUserOrgName')
        if (orgName) currentUserOrgName.value = orgName
        console.log('🔍 REFEREE MODE: Creating report for current user')
        console.log('  - currentUserID:', currentUserID.value)
        console.log('  - currentUserOrgName:', currentUserOrgName.value)
      }
      
      resolve()
    })
  })
}

// --- Firestore fetches ---
async function fetchMatchReportData() {
  if (!mreportId) return
  const docRef = doc(db, 'matchreports', mreportId)
  const snap = await getDoc(docRef)
  if (!snap.exists()) return
  const d = snap.data()
  Object.assign(mreport, {
    GameNumber: d.GameNumber || '',
    GameDate: d.GameDate instanceof Timestamp ? d.GameDate.toDate().toISOString().split('T')[0] : (d.GameDate || ''),
    GameTime: d.GameTime || '',
    Venue: d.Venue || '',
    League: d.League || '',
    LeagueTypeSeason: d.LeagueTypeSeason || '',
    GenderAge: d.GenderAge || '',
    TeamHome: d.TeamHome || '',
    TeamAway: d.TeamAway || '',
    TeamColorHome: d.TeamColorHome || '',
    TeamColorAway: d.TeamColorAway || '',
    ScoreHome: d.ScoreHome ?? 0,
    ScoreAway: d.ScoreAway ?? 0,
    Overtime: d.Overtime ?? false,
    Penalties: d.Penalties ?? false,
    MatchComments: d.MatchComments || '',
    REFName: d.REFName || '',
    AR1Name: d.AR1Name || '',
    AR2Name: d.AR2Name || '',
    REF4THName: d.REF4THName || '',
    BhvrCoachHomeRating: d.BhvrCoachHomeRating || '',
    BhvrCoachHomeNotes: d.BhvrCoachHomeNotes || '',
    BhvrCoachAwayRating: d.BhvrCoachAwayRating || '',
    BhvrCoachAwayNotes: d.BhvrCoachAwayNotes || '',
    BhvrSpectatorHomeRating: d.BhvrSpectatorHomeRating || '',
    BhvrSpectatorHomeNotes: d.BhvrSpectatorHomeNotes || '',
    BhvrSpectatorAwayRating: d.BhvrSpectatorAwayRating || '',
    BhvrSpectatorAwayNotes: d.BhvrSpectatorAwayNotes || '',
    YellowCardCount: d.YellowCardCount ?? 0,
    RedCardCount: d.RedCardCount ?? 0,
    PurpleCardCount: d.PurpleCardCount ?? 0,
    DoublePurpleCardCount: d.DoublePurpleCardCount ?? 0,
    UserID: d.UserID || currentUserID.value,
    Organization: d.Organization || currentUserOrgName.value,
  })
}

async function fetchMisconductData() {
  if (!mreportId) return
  const q = query(misconductColRef, where('MatchReportID', '==', mreportId))
  const snap = await getDocs(q)
  misconductReports.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
  misconductReports.value.sort((a, b) => (Number(a.Time) || 0) - (Number(b.Time) || 0))
}

async function fetchLeagues() {
  const snap = await getDocs(leaguesColRef)
  leagues.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

async function fetchGenderAges() {
  const snap = await getDocs(genderagesColRef)
  genderages.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

async function buildRefereeListInSameOrgName() {
  const orgName = localStorage.getItem('currentUserOrgName')
  if (!orgName) return
  const snap = await getDocs(query(refereesColRef, where('Organization', '==', orgName)))
  const list = snap.docs.map(d => {
    const u = d.data()
    const fullName = `${u.NameFirst || 'N/A'} ${u.NameLast || 'N/A'}`
    return { id: d.id, fullName, NameFirst: u.NameFirst, NameLast: u.NameLast }
  })
  list.sort((a, b) => (a.NameLast || '').localeCompare(b.NameLast || ''))
  users.value = list
}

// --- Game search ---
async function loadGames() {
  const orgName = localStorage.getItem('currentUserOrgName')
  if (!orgName) return

  const reportsSnap = await getDocs(query(matchreportsColRef, where('Organization', '==', orgName)))
  const reported = new Set(reportsSnap.docs.map(d => d.data().GameNumber).filter(Boolean))

  const gamesSnap = await getDocs(gamescheduleColRef)
  const today = new Date(); today.setHours(0,0,0,0)
  const sevenDaysFromNow = new Date(today); 
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7)

  games.value = gamesSnap.docs.map(docu => {
    const g = docu.data()
    let gd = g.GameDate
    if (gd instanceof Timestamp) gd = gd.toDate()
    else if (typeof gd === 'string') gd = new Date(gd)
    else gd = new Date()
    gd.setHours(0,0,0,0)
    return {
      GameNumber: g.GameNumber || '',
      GameDate: gd,
      GameTime: g.GameTime || '',
      League: g.League || '',
      GenderAge: g.GenderAge || '',
      TeamHome: g.TeamHome || '',
      TeamAway: g.TeamAway || '',
      Venue: g.Venue || '',
      REF: g.REF || '',
      AR1: g.AR1 || '',
      AR2: g.AR2 || '',
      REF4TH: g.REF4TH || '',
      MatchReportSubmitted: reported.has(g.GameNumber),
    }
  }).filter(g => g.GameDate <= sevenDaysFromNow)

  games.value.sort((a, b) => (b.GameDate - a.GameDate) || ((a.GameNumber||0) - (b.GameNumber||0)))
  filteredGames.value = games.value
}

function filterGames() {
  let filtered = games.value

  // Filter by date if provided
  if (searchDate.value) {
    const wanted = new Date(searchDate.value)
    wanted.setHours(0,0,0,0)
    filtered = filtered.filter(g => g.GameDate.getTime() === wanted.getTime())
  }

  // Filter by referee last name if provided
  if (searchRefName.value) {
    const searchTerm = searchRefName.value.toLowerCase().trim()
    filtered = filtered.filter(g => {
      const ref = (g.REF || '').toLowerCase()
      const ar1 = (g.AR1 || '').toLowerCase()
      const ar2 = (g.AR2 || '').toLowerCase()
      const ref4th = (g.REF4TH || '').toLowerCase()
      
      return ref.includes(searchTerm) || 
             ar1.includes(searchTerm) || 
             ar2.includes(searchTerm) || 
             ref4th.includes(searchTerm)
    })
  }

  filteredGames.value = filtered
}

function formatDate(d) {
  const opts = { year: '2-digit', month: '2-digit', day: '2-digit' }
  return new Date(d).toLocaleDateString('en-US', opts)
}

function selectGame(g) {
  mreport.GameNumber = g.GameNumber
  mreport.GameDate = g.GameDate instanceof Date ? g.GameDate.toISOString().split('T')[0] : ''
  mreport.GameTime = g.GameTime
  mreport.Venue = g.Venue
  mreport.League = g.League
  mreport.GenderAge = g.GenderAge
  mreport.TeamHome = g.TeamHome
  mreport.TeamAway = g.TeamAway
  mreport.TeamColorAway = g.TeamColorAway || 'White'
  mreport.Venue = g.Venue
  mreport.REFName = g.REF
  mreport.AR1Name = g.AR1
  mreport.AR2Name = g.AR2
  mreport.REF4THName = g.REF4TH
  openGameSearch.value = false
}

watch(openGameSearch, async (val) => {
  if (val) {
    searchDate.value = ''
    searchRefName.value = ''
    await loadGames()
  }
})

// --- Save/Cancel ---
async function onSave(skipRouting = false) {
  const ok = await formRef.value?.validate()
  if (!ok?.valid) return
  saving.value = true
  try {
    const gameDateTs = mreport.GameDate
      ? Timestamp.fromDate(new Date(mreport.GameDate + 'T12:00:00Z'))
      : Timestamp.fromDate(new Date('2099-12-31T12:00:00Z'))

    if (isNew.value || !mreportId) {
      console.log('🔍 SAVING NEW REPORT:')
      console.log('  - UserID:', currentUserID.value)
      console.log('  - Organization:', currentUserOrgName.value)
      
      const newRef = await addDoc(matchreportsColRef, {
        ...mreport,
        GameDate: gameDateTs,
        UserID: currentUserID.value,
        Organization: currentUserOrgName.value,
      })
      mreportId = newRef.id
      isNew.value = false
      
      console.log('✅ Report saved with ID:', newRef.id)
      console.log('✅ Saved with UserID:', currentUserID.value, 'and Organization:', currentUserOrgName.value)
    } else {
      const countSnap = await getDocs(query(misconductColRef, where('MatchReportID', '==', mreportId)))
      let yc = 0, rc = 0, pc = 0, dpc = 0, warn = 0, removal = 0
      countSnap.forEach(docu => {
        const x = docu.data()
        if (x.RefereeAction === 'Yellow Card') yc++
        else if (x.RefereeAction === 'Red Card') rc++
        else if (x.RefereeAction === 'Purple Card') pc++
        else if (x.RefereeAction === 'Double Purple Card') dpc++
        else if (x.RefereeAction === 'WARN') warn++
        else if (x.RefereeAction === 'REMOVAL') removal++
      })

      Object.assign(mreport, { YellowCardCount: yc, RedCardCount: rc, PurpleCardCount: pc, DoublePurpleCardCount: dpc, WarnCount: warn, RemovalCount: removal })

      await updateDoc(doc(db, 'matchreports', mreportId), {
        ...mreport,
        GameDate: gameDateTs,
        Organization: currentUserOrgName.value,
        YellowCardCount: yc,
        RedCardCount: rc,
        PurpleCardCount: pc,
        DoublePurpleCardCount: dpc,
        WarnCount: warn,
        RemovalCount: removal,
      })
    }

    await nextTick()
    if (!skipRouting) routeToDashboard()
  } catch (e) {
    console.error('Error saving match report:', e)
  } finally {
    saving.value = false
  }
}

function routeToDashboard() {
  const role = localStorage.getItem('currentUserRole')
  const path = role === 'ADMIN' ? '/dashboard-admin' : role === 'RefereeCoach' ? '/dashboard-refcoach' : '/dashboard-referee'
  router.push(path)
}

async function onCancel() {
  routeToDashboard()
}

const rowById = (id) => misconductReports.value.find(r => r?.id === id) || null

function onRowClick(_e, ctx) {
  // Try to get the row data from various possible locations in the context
  let row = null;
  
  // Method 1: Direct item access
  if (ctx?.item) {
    row = ctx.item;
  }
  
  // Method 2: Raw item access
  if (!row && ctx?.item?.raw) {
    row = ctx.item.raw;
  }
  
  // Method 3: Value access
  if (!row && ctx?.item?.value) {
    row = typeof ctx.item.value === 'object' ? ctx.item.value : null;
  }
  
  // Method 4: Internal item access
  if (!row && ctx?.internalItem?.value) {
    row = ctx.internalItem.value;
  }
  
  // If we found a row object with an id, edit it
  if (row && row.id) {
    onEditMisconduct(row.id);
  }
}

// --- Misconduct dialog actions ---
async function onAddMisconduct() {
  if (!mreportId) {
    console.log('🔍 SAVING REPORT FOR MISCONDUCT:')
    console.log('  - UserID:', currentUserID.value)
    console.log('  - Organization:', currentUserOrgName.value)
    
    await onSave(true)
    if (!mreportId) return
    
    console.log('✅ Report saved with UserID:', currentUserID.value)
  }
  misconductDialog.miscId = null
  misconductDialog.open = true
}

function onEditMisconduct(idOrRow) {
  const row = typeof idOrRow === 'object' ? idOrRow : rowById(idOrRow)
  if (!row) return

  const roleLS = (localStorage.getItem('currentUserRole') || '').toUpperCase()
  const canAny = !!(caps?.value?.canEditAnyMisconduct) || roleLS === 'ADMIN'

  if (!canAny) {
    const owner = row.MatchReportUserID ?? row.UserID ?? null
    const me = currentUserID.value || null
    if (owner && me && owner !== me) {
      return
    }
  }

  misconductDialog.miscId = row.id
  misconductDialog.open  = true
}

async function onDeleteMisconduct(id) {
  if (!confirm('Delete this misconduct report?')) return
  await deleteDoc(doc(misconductColRef, id))
  await fetchMisconductData()
}

async function onMisconductSaved() {
  misconductDialog.open = false
  await fetchMisconductData()
  let yc = 0, rc = 0, pc = 0, dpc = 0, warn = 0, removal = 0
  misconductReports.value.forEach(x => {
    if (x.RefereeAction === 'Yellow Card') yc++
    else if (x.RefereeAction === 'Red Card') rc++
    else if (x.RefereeAction === 'Purple Card') pc++
    else if (x.RefereeAction === 'Double Purple Card') dpc++
    else if (x.RefereeAction === 'WARN') warn++
    else if (x.RefereeAction === 'REMOVAL') removal++
  })
  Object.assign(mreport, { YellowCardCount: yc, RedCardCount: rc, PurpleCardCount: pc, DoublePurpleCardCount: dpc, WarnCount: warn, RemovalCount: removal })
}

function onMisconductClosed() {
  misconductDialog.open = false
}

function getMisconductRowClass(row) {
  switch (row.RefereeAction) {
    case 'Yellow Card': return 'mis-row--yellow'
    case 'Red Card': return 'mis-row--red'
    case 'Purple Card': return 'mis-row--purple'
    case 'Double Purple Card': return 'mis-row--doublepurple'
    default: return '';
  }
}
</script>

<style scoped>
/* ===== FIXED LAYOUT STRUCTURE ===== */
.cl-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background-color: #f5f5f5;
  overflow: hidden; /* Prevent page-level scroll */
  position: fixed;
  top: 0;
  left: 0;
}

/* Fixed Header */
.cl-toolbar {
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  height: 60px;
  z-index: 1000;
  border-top: 5px solid #02b8cc;
  border-bottom: 1px solid #32e3f7;
  background-color: #005ce6;
  flex-shrink: 0;
}

.cl-toolbar :deep(.v-toolbar) {
  background: transparent !important;
}

.cl-toolbar :deep(.v-toolbar-title),
.cl-toolbar-title {
  color: #fff;
  font-size: 20px;
  font-weight: bold;
}

.save-button {
  border-radius: 50px !important;
  padding: 0 30px !important;
  background-color: #4CAF50 !important;
  color: white !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2) !important;
}

.save-button:hover {
  background-color: #45a049 !important;
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.3) !important;
}

/* Scrollable Content Area */
.cl-content-area {
  margin-top: 140px; /* Account for fixed toolbar (80px + 60px) */
  flex: 1;
  overflow-y: scroll !important; /* Always show vertical scrollbar */
  overflow-x: scroll !important; /* Always show horizontal scrollbar */
  padding-bottom: 40px;
  padding-top: 0;
  position: relative;
  width: 100%;
  height: calc(100vh - 140px);
}

/* Form Container */
.cl-form {
  min-width: 1600px; /* Force horizontal scroll when zoomed */
  padding: 0 30px 30px 30px;
  padding-right: 24px;
}

.cl-form :deep(.v-container) {
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* Form Content Structure */
.form-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 16px;
  min-width: 1600px; /* Ensure content is wide enough to trigger horizontal scroll */
}

.report-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 1550px; /* Ensure section is wide enough */
}

/* Misconduct Section */
.misconduct-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 400px;
  min-width: 1550px; /* Ensure section is wide enough */
}

.misconduct-card {
  overflow: visible;
}

/* ===== CUSTOM SCROLLBAR ===== */
.cl-content-area::-webkit-scrollbar {
  width: 18px !important;
  height: 18px !important;
}

.cl-content-area::-webkit-scrollbar-track {
  background: #d0d0d0;
  border-radius: 0;
}

.cl-content-area::-webkit-scrollbar-thumb {
  background: #666;
  border-radius: 0;
  border: 2px solid #d0d0d0;
}

.cl-content-area::-webkit-scrollbar-thumb:hover {
  background: #444;
}

.cl-content-area::-webkit-scrollbar-corner {
  background: #d0d0d0;
}

/* ===== FORM STYLING ===== */
input {
  font-family: 'Arial', sans-serif;
  font-size: 15px;
  color: #b71212;
  height: 30px;
}

select {
  font-family: 'Arial', sans-serif;
  font-size: 16px;
  color: #b71212;
}

/* All fields default to white */
:deep(.v-field) {
  background-color: white !important;
}

/* Only v-text-field and v-select in first v-row (required fields) - light green when empty */
.report-section > .v-row:first-child :deep(.v-text-field:not(.v-input--dirty) .v-field),
.report-section > .v-row:first-child :deep(.v-select:not(.v-input--dirty) .v-field) {
  background-color: rgb(215, 251, 215) !important;
}

/* Behavior assessment selects (rating fields) - light green when empty */
.report-section :deep(.v-row.pl-10 .v-select:not(.v-input--dirty) .v-field) {
  background-color: rgb(215, 251, 215) !important;
}

/* Textareas always white */
.report-section :deep(.v-textarea .v-field) {
  background-color: white !important;
}

/* Autocomplete fields always white */
.report-section :deep(.v-autocomplete .v-field) {
  background-color: white !important;
}

/* Fields that have been filled (dirty) - white */
:deep(.v-input--dirty .v-field) {
  background-color: white !important;
}

/* Error state - light yellow */
:deep(.v-input--error .v-field) {
  background-color: rgb(250, 250, 219) !important;
}

/* Focused fields - white */
:deep(.v-field--focused) {
  background-color: white !important;
}

/* ===== MISCONDUCT TABLE STYLING ===== */
.misconduct-area .v-data-table-header th,
.misconduct-area .v-data-table td {
  text-align: left !important;
}

.v-data-table tbody tr {
  cursor: pointer;
}

::v-deep(.mis-row--yellow) {
  background-color: rgba(255, 235, 59, 0.15) !important;
}

::v-deep(.mis-row--red) {
  background-color: rgba(244, 67, 54, 0.14) !important;
}

::v-deep(.mis-row--purple) {
  background-color: rgba(156, 39, 176, 0.1) !important;
}

::v-deep(.mis-row--doublepurple) {
  background-color: rgba(103, 58, 183, 0.15) !important;
}

:deep(.v-data-table tbody tr:hover > td) {
  filter: brightness(0.98);
}

::v-deep(.fixed-layout table) {
  width: 100%;
}

::v-deep(.fixed-layout td) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== RATING DIALOG ===== */
.rating-scale-text {
  white-space: normal;
  word-break: break-word;
  font-size: 15px;
  line-height: 1.5;
}

.rating-scale-text p {
  margin-bottom: 12px;
}

/* ===== GAME SEARCH TABLE ===== */
.game-search-table-wrapper {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 420px;
}

.game-search-table-wrapper::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.game-search-table-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 5px;
}

.game-search-table-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 5px;
}

.game-search-table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.game-search-table-wrapper .v-table {
  min-width: 1140px;
  table-layout: fixed;
  width: 100%;
}

.game-search-table-wrapper .v-table th,
.game-search-table-wrapper .v-table td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== RESPONSIVE ADJUSTMENTS ===== */
@media (max-width: 1400px) {
  .cl-form {
    min-width: 1400px; /* Increase min-width for smaller screens */
  }
}

@media (max-width: 768px) {
  .cl-toolbar {
    top: 60px;
    height: 50px;
  }
  
  .cl-content-area {
    margin-top: 110px;
  }
  
  .cl-form {
    padding: 16px;
    min-width: 1200px;
  }
}
</style>